import { Avatar, AvatarImage, AvatarFallback, AvatarInitials } from "@/components/Avatar";
import { InsightsTopTable } from "@/components/insights/InsightsTopTable";
import { formatSeconds } from "@/format/time";
import { useMostActiveUsers, useTopEnvironmentClasses, useTopProjects } from "@/queries/insights-queries";
import { type TimeRange } from "@/routes/insights/insights";
import type { Timestamp } from "@bufbuild/protobuf";
import { Text } from "@/components/podkit/typography/Text";
import { useMemo, type FC, useEffect, useRef } from "react";

type Props = {
    startTimestamp: Timestamp;
    endTimestamp: Timestamp;
    selectedRange: TimeRange;
    projectId?: string;
    projectName?: string;
    highlightMostActiveUsers?: boolean;
};
export const InsightsTopTablesList: FC<Props> = ({
    startTimestamp,
    endTimestamp,
    selectedRange,
    projectId,
    projectName,
    highlightMostActiveUsers,
}) => {
    const {
        data: topUsers,
        isLoading: isLoadingTopUsers,
        fetchNextPage: fetchNextUsersPage,
        isFetchingNextPage: isFetchingNextUsersPage,
    } = useMostActiveUsers(startTimestamp, endTimestamp, selectedRange, projectId);

    const topActiveUsersData = useMemo(() => {
        const data = topUsers?.pages
            .flatMap((page) => page.users)
            .map((user) => ({
                title: (
                    <div className="flex items-center gap-2">
                        <Avatar size={16}>
                            {user.userAvatarUrl && (
                                <AvatarImage
                                    src={user.userAvatarUrl}
                                    alt={`${user.userName || user.userId}'s avatar`}
                                />
                            )}
                            <AvatarFallback>
                                <AvatarInitials name={user.userName || user.userId} size={16} />
                            </AvatarFallback>
                        </Avatar>
                        {/* for deleted users, we show at least a user ID */}
                        <Text className="text-sm">{user.userName || user.userId}</Text>
                    </div>
                ),
                time: formatSeconds(user.totalRuntimeSeconds),
                key: user.userId,
            }));
        return data ?? [];
    }, [topUsers]);

    const { data: topEnvironmentClasses, isLoading: isLoadingTopEnvironmentClasses } = useTopEnvironmentClasses(
        startTimestamp,
        endTimestamp,
        selectedRange,
        projectId,
    );

    const topEnvironmentClassesData = useMemo(() => {
        const data = topEnvironmentClasses?.pages
            .flatMap((page) => page.environmentClasses)
            .map((environmentClass) => ({
                title: environmentClass.environmentClassName ? (
                    <div className="">
                        <Text className="text-sm">{environmentClass.environmentClassName}</Text>
                        <Text className="text-sm text-content-secondary">{environmentClass.runnerName}</Text>
                    </div>
                ) : (
                    <Text className="text-sm">{environmentClass.runnerName}</Text>
                ),
                time: formatSeconds(Number(environmentClass.totalRuntimeSeconds)),
                key: environmentClass.environmentClassId || `${environmentClass.runnerName}-unknown`,
            }));
        return data ?? [];
    }, [topEnvironmentClasses]);

    const {
        data: topProjects,
        isLoading: isLoadingTopProjects,
        fetchNextPage: fetchNextProjectsPage,
        isFetchingNextPage: isFetchingNextProjectsPage,
    } = useTopProjects(startTimestamp, endTimestamp, selectedRange);

    const topProjectsData = useMemo(() => {
        const data = topProjects?.pages
            .flatMap((page) => page.projects)
            .map((project) => ({
                title: project.projectName ? (
                    project.projectName
                ) : (
                    <Text className="text-sm text-content-secondary">
                        Unknown project {project.projectId.split("-").at(-1)}
                    </Text>
                ),
                time: formatSeconds(Number(project.totalRuntimeSeconds)),
                key: project.projectId,
            }));
        return data ?? [];
    }, [topProjects]);

    // Scroll and highlight logic for Most Active Users
    const mostActiveUsersRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (highlightMostActiveUsers && mostActiveUsersRef.current) {
            mostActiveUsersRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [highlightMostActiveUsers]);

    const mostActiveUsersTitle = projectName ? `Most active users of ${projectName}` : "Most active users";
    return (
        <div className="mx-auto grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div
                id="most-active-users"
                ref={mostActiveUsersRef}
                className={
                    highlightMostActiveUsers ? "ring-accent bg-accent/10 ring-2 transition-all duration-500" : undefined
                }
            >
                <InsightsTopTable
                    title={mostActiveUsersTitle}
                    subtitle={selectedRange.label}
                    isLoading={isLoadingTopUsers || isFetchingNextUsersPage}
                    rows={topActiveUsersData ?? []}
                    onScrollEnd={fetchNextUsersPage}
                />
            </div>

            <InsightsTopTable
                title="Top projects"
                subtitle={selectedRange.label}
                isLoading={isLoadingTopProjects || isFetchingNextProjectsPage}
                rows={topProjectsData ?? []}
                onScrollEnd={fetchNextProjectsPage}
            />

            <InsightsTopTable
                title="Top environment classes"
                subtitle={selectedRange.label}
                isLoading={isLoadingTopEnvironmentClasses}
                rows={topEnvironmentClassesData ?? []}
            />
        </div>
    );
};
